import * as THREE from 'three';
import { animateTransform } from '../scene-transforms.js';

/** A few incommensurate waves: a flicker with no visible period. Around 0, within about ±1. */
export const waver = (time: number, phase: number) => Math.sin(time * 7.3 + phase) * .45 + Math.sin(time * 12.9 + phase * 2.3) * .3 + Math.sin(time * 3.1 + phase * .7) * .35;

export interface FireOptions {
  /** Overall scale: 1 is a campfire about 1.5 m tall. */
  size: number;
  /** Radius of the bed of glowing coals under the flames; 0 leaves the bed to the caller. */
  bed?: number;
  /** How many sparks are rising at once. */
  embers?: number;
  /** Varies the flicker between fires. */
  seed?: number;
}

export interface Fire {
  group: THREE.Group;
  /** Shared by the coals, for callers that add their own. */
  coals: THREE.MeshStandardMaterial;
  /** Animates the fire and returns its brightness this frame (about 0.85–1.15), for the light it throws. */
  update(time: number): number;
}

/**
 * A fire: a faceted flame that breathes around a turning core, a bed of coals that glows, and sparks that rise, drift and
 * wink out.
 */
export function createFire(options: FireOptions): Fire {
  const { size, bed = 0, embers = Math.round(8 * size), seed = 0 } = options;
  const group = new THREE.Group();
  // The crossroads camp's fire: one faceted orange cone that breathes, and a pale core turning inside it. Both snake from
  // side to side, a wave running up from the root so the tip swings widest.
  const swayTime = { value: 0 };
  const cone = (radius: number, height: number, sides: number, color: number, lag: number) => {
    const geometry = new THREE.ConeGeometry(radius, height, sides, 6); geometry.translate(0, height / 2, 0);
    const material = new THREE.MeshBasicMaterial({ color });
    material.customProgramCacheKey = () => 'fire-sway';
    material.onBeforeCompile = shader => {
      Object.assign(shader.uniforms, { uSwayTime: swayTime, uSwayPhase: { value: seed * 1.7 + lag }, uSwayHeight: { value: height } });
      shader.vertexShader = 'uniform float uSwayTime, uSwayPhase, uSwayHeight;\n' + shader.vertexShader.replace('#include <begin_vertex>', `#include <begin_vertex>
        float rise = transformed.y / uSwayHeight;
        transformed.x += sin(rise * 3.2 - uSwayTime * 2.6 + uSwayPhase) * .16 * rise * uSwayHeight;
        transformed.z += sin(rise * 2.6 - uSwayTime * 2.1 + uSwayPhase * 1.3) * .08 * rise * uSwayHeight;`);
    };
    const mesh = animateTransform(new THREE.Mesh(geometry, material)); group.add(mesh); return mesh;
  };
  const flame = cone(.42, 1.35, 7, 0xff9f35, 0), core = cone(.2, .85, 5, 0xffdc80, .5);
  flame.scale.setScalar(size); core.scale.setScalar(size); core.position.y = .05 * size;
  const coals = new THREE.MeshStandardMaterial({ color: 0x2a1a14, emissive: 0xb02e0a, emissiveIntensity: .7, roughness: .9, flatShading: true });
  if (bed > 0) {
    const geometry = new THREE.DodecahedronGeometry(.06 * Math.sqrt(size), 0), count = Math.max(6, Math.round(bed * bed * 30));
    const pieces = new THREE.InstancedMesh(geometry, coals, count), matrix = new THREE.Matrix4(), turn = new THREE.Quaternion(), place = new THREE.Vector3(), scale = new THREE.Vector3();
    for (let i = 0; i < count; i++) {
      const a = i * 2.39996 + seed, r = bed * Math.sqrt((i + .5) / count);
      turn.setFromEuler(new THREE.Euler(i * 1.3, i * 2.1, i * .7)); scale.setScalar(.7 + (i * 37 % 10) / 20);
      pieces.setMatrixAt(i, matrix.compose(place.set(Math.cos(a) * r, .04 * size, Math.sin(a) * r), turn, scale));
    }
    pieces.receiveShadow = true; group.add(pieces);
  }
  // Sparks: tiny additive chips whose paths are fixed by their index, so the same moment always looks the same.
  const sparkMaterial = new THREE.MeshBasicMaterial({ color: 0xffb862, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  const sparks = new THREE.InstancedMesh(new THREE.OctahedronGeometry(.028 * Math.sqrt(size), 0), sparkMaterial, Math.max(1, embers));
  sparks.count = embers; sparks.frustumCulled = false; group.add(sparks);
  const sparkMatrix = new THREE.Matrix4(), sparkAt = new THREE.Vector3(), sparkScale = new THREE.Vector3(), still = new THREE.Quaternion();
  return { group, coals, update(time) {
    const brightness = 1 + waver(time * .6, seed) * .08;
    swayTime.value = time;
    const breath = Math.sin(time * 3.1 + seed) * .6 + Math.sin(time * 4.7 + seed * 2) * .4;
    flame.scale.set(size * (1 + breath * .04), size * (.95 + breath * .06), size * (1 + breath * .04));
    core.rotation.y = time * .6 + seed;
    coals.emissiveIntensity = .65 + waver(time * .35, seed + 4) * .3;
    for (let i = 0; i < embers; i++) {
      const life = (time * (.32 + (i * 13 % 7) * .03) + i / embers) % 1, a = i * 2.39996 + seed;
      sparkAt.set(Math.cos(a) * .25 * size + Math.sin(time * 1.7 + i) * .25 * life * size + life * .35 * size, (.35 + life * 2.6) * size, Math.sin(a) * .25 * size);
      sparks.setMatrixAt(i, sparkMatrix.compose(sparkAt, still, sparkScale.setScalar(Math.sin(Math.PI * life) * (life < .9 ? 1 : 0))));
    }
    sparks.instanceMatrix.needsUpdate = true;
    return brightness;
  } };
}
